import { useEffect, useState } from 'react';
import { Box, Typography, Divider, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { Namespace } from '../../../locales/translations';
import { selectSelectedSurveyTemplate, selectSelectedSurveyTemplateLoading } from '../../../store/reducers/surveyTemplates/selected';
import SurveyTemplatesController from '../../../actions/survey_templates';
import { usePartnerID } from '../../../hooks/partners';
import FieldRenderer from './FieldRenderer';
import { SurveyResponseValue } from '../../../models/SurveyTemplates';
import { SensitizationReportDbData, SensitizationReport } from '../../../models/AwarenessCampaign';
import AddNewAddressForm from './AddNewAddressForm';
import { formatAddress } from '../../../helpers/geo';
import SubmitButton from './SubmitButton';
import CampaignAddressesMethods from '../../../actions/campaignAddresses';
import { AddressReportActions } from '../../../store/reducers/awareness_campaigns/address_report';
import { showTranslatedMessage } from '../../../store/reducers/snacks';
import { useParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import {  useUserName } from '../../../hooks/user';

interface DynamicAddressFormProps {
    onFormDataChange?: (formData: { [fieldId: string]: SurveyResponseValue }) => void;
}

export default function DynamicAddressForm({ onFormDataChange }: DynamicAddressFormProps) {
    const { t } = useTranslation([Namespace.ADDRESSES, Namespace.REPORT]);
    const dispatch = useAppDispatch();
    const partnerID = usePartnerID();
    const { missionID } = useParams<{ missionID: string }>();
    
    // Redux state
    const userEmail = useAppSelector(state => state.user.email)!;
    const selectedCampaign = useAppSelector(state => state.awareness_campaign.selected.selectedAwarenessCampaign);
    const selectedTemplate = useAppSelector(selectSelectedSurveyTemplate);
    const templateLoading = useAppSelector(selectSelectedSurveyTemplateLoading);
    const selectedAddress = useAppSelector((state) => state.awareness_campaign.address_report.selectedAddress);
    const addressLabel = selectedAddress ? formatAddress(selectedAddress.address) : "";
    const newAddress = useAppSelector((state) => state.awareness_campaign.address_report.newAddress);

    // Dynamic form data
    const [formData, setFormData] = useState<{ [fieldId: string]: SurveyResponseValue }>({});

    // Load survey template when campaign is selected
    useEffect(() => {
        if (selectedCampaign?.surveyTemplateID && partnerID) {
            dispatch(SurveyTemplatesController.getById(partnerID, selectedCampaign.surveyTemplateID));
        }
    }, [selectedCampaign?.surveyTemplateID, partnerID, dispatch]);

    // Load existing report data when editing an address
    useEffect(() => {
        if (selectedAddress?.report && 'responses' in selectedAddress.report) {
            const existingReport = selectedAddress.report as SensitizationReport;
            const loadedData: { [fieldId: string]: SurveyResponseValue } = {};
            
            // Extract values from the responses
            Object.entries(existingReport.responses).forEach(([componentId, response]) => {
                loadedData[componentId] = response.value;
            });
            
            setFormData(loadedData);
        }
    }, [selectedAddress]);

    // Update parent component when form data changes
    useEffect(() => {
        if (onFormDataChange) {
            onFormDataChange(formData);
        }
    }, [formData, onFormDataChange]);

    const handleDynamicFieldChange = (fieldId: string, value: SurveyResponseValue) => {
        setFormData(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const userName = useUserName();

    const handleSubmit = async () => {
        if (!partnerID || !missionID || !selectedTemplate) return;

        // Build the SensitizationReport
        const report: SensitizationReportDbData = {
            templateID: selectedTemplate.ID,
            responses: {},
            submittedAt: Timestamp.now(),
            submittedBy: {
                name: userName,
                email: userEmail
            },
            isComplete: true
        };

        // Build responses from form data
        selectedTemplate.components.forEach(component => {
            const value = formData[component.id];
            if (value !== undefined && value !== null) {
                // Special handling for image uploads
                if (component.type === 'image_upload' && Array.isArray(value)) {
                    // Check if we have existing paths (strings) or new files
                    if (value.length === 0) {
                        // No images, skip this field
                        return;
                    } else if (typeof value[0] === 'string') {
                        // Existing image paths, keep them as is
                        report.responses[component.id] = {
                            componentID: component.id,
                            componentType: component.type,
                            label: component.label,
                            value: value
                        };
                    } else {
                        // New File objects to be uploaded
                        report.responses[component.id] = {
                            componentID: component.id,
                            componentType: component.type,
                            label: component.label,
                            value: value
                        };
                    }
                } else {
                    // All other field types
                    report.responses[component.id] = {
                        componentID: component.id,
                        componentType: component.type,
                        label: component.label,
                        value: value
                    };
                }
            }
        });

        try {
            if (selectedAddress) {
                // Updating an existing address
                await dispatch(CampaignAddressesMethods.updateCampaignAddressWithTemplate(
                    partnerID,
                    missionID,
                    selectedAddress.ID,
                    report
                ));
            } else {
                // Adding a new address
                if (!newAddress.street || !newAddress.city || !newAddress.postalCode) {
                    let missingField = 'city';
                    if (!newAddress.postalCode) missingField = 'postalCode';
                    if (!newAddress.street) missingField = 'street';
                    dispatch(showTranslatedMessage({
                        variant: 'error',
                        messageKey: `error.missing_${missingField}`,
                        context: { ns: Namespace.REPORT }
                    }));
                    return;
                }

                const addressData = {
                    address: newAddress,
                    lat: 0, // TODO: make lat and lng optional instead
                    lng: 0
                };

                const ambassador = {
                    email: userEmail,
                    name: userName
                };

                await dispatch(CampaignAddressesMethods.addSensitizedAddressWithTemplate(
                    partnerID,
                    missionID,
                    addressData,
                    report,
                    ambassador
                ));
            }

            dispatch(AddressReportActions.closeReportForm());
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    // Show loading indicator while template is being loaded
    if (templateLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    // If no template is found after loading, show error message
    if (!selectedTemplate) {
        return (
            <Alert severity="warning" sx={{ mt: 2 }}>
                {t('no_form_template_found', { ns: Namespace.REPORT })}
            </Alert>
        );
    }

    // Render dynamic form based on survey template
    return (
        <Box>
            {/* Address Section */}
            {addressLabel ? ( // address already added to the campaign
                <Typography variant='h3'>
                    {t('intervention.question', { ns: Namespace.REPORT, address: addressLabel })}
                </Typography>
            ) : ( // adding a new address to the campaign
                <>
                    <Typography variant='h3'>
                        {t('intervention.add_new_address', { ns: Namespace.REPORT })}
                    </Typography>
                    <AddNewAddressForm />
                    <Divider variant="fullWidth" sx={{ my: 3 }} />
                </>
            )}
            <Divider sx={{ my: 3 }} />

            {/* Dynamic Survey Fields */}
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('survey_information', { ns: Namespace.REPORT })}
            </Typography>
            
            {[...selectedTemplate.components]
                .sort((a, b) => a.position - b.position)
                .map((component) => (
                    <Box key={component.id} sx={{ mb: 2 }}>
                        <FieldRenderer
                            field={component}
                            value={formData[component.id]}
                            onChange={(value) => handleDynamicFieldChange(component.id, value)}
                        />
                    </Box>
                ))}

            <SubmitButton onSubmit={handleSubmit} />  
        </Box>
    );
}
